import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Power, Wifi, Volume2, Accessibility, Eye, EyeOff, User, ChevronRight,
  RotateCcw, Moon, Settings as SettingsIcon, Maximize2, Loader2, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import LinuxBootScreen from "@/components/linux/LinuxBootScreen";
import LinuxShutdownScreen from "@/components/linux/LinuxShutdownScreen";
import { InstallPrepIT } from "@/components/InstallPrepIT";

const WALLPAPER = "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/7e442fd65cc7f86c0856f5e827a4e6d10ae264408889559d49ae44f3bd3fd681.jpeg";

const useClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
};

function SessionLoader({ onDone }) {
  const steps = ["Loading student profile…", "Restoring lab environment…", "Starting terminal service…", "Starting desktop session…", "Ready"];
  const [i, setI] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timers = [];
    let t = 0;
    steps.forEach((_, idx) => { t += reduce ? 120 : 380 + Math.random() * 200; timers.push(setTimeout(() => setI(idx), t)); });
    timers.push(setTimeout(() => onDone && onDone(), t + 500));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black text-white gdm-fade" data-testid="linux-session-loader"
      style={{ backgroundImage: `linear-gradient(rgba(6,8,12,0.86),rgba(6,8,12,0.94)), url(${WALLPAPER})`, backgroundSize: "cover", backgroundPosition: "center" }}>
      <Loader2 className="h-9 w-9 text-[#38bdf8] gdm-spin mb-5" />
      <div className="font-display text-lg font-semibold">Welcome</div>
      <div className="text-sm text-white/60 mt-1 font-mono">{steps[i]}</div>
    </div>
  );
}

export default function Login() {
  const { login, refresh, user } = useAuth();
  const navigate = useNavigate();
  const booted = typeof sessionStorage !== "undefined" && sessionStorage.getItem("hmg_booted") === "1";
  const [phase, setPhase] = useState(booted ? "LOGIN_SCREEN" : "BOOTING");
  const now = useClock();

  const [mode, setMode] = useState("signin"); // signin | signup
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [powerOpen, setPowerOpen] = useState(false);
  const [qsOpen, setQsOpen] = useState(false);
  const [wifi, setWifi] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    // Auto-enter only for already-authenticated returning users (not mid-login,
    // which is handled by the STARTING_SESSION animation).
    if (user && (phase === "BOOTING" || phase === "LOGIN_SCREEN")) {
      navigate(destFor(user), { replace: true });
    }
  }, [user, phase, navigate]);

  const destFor = (u) => u?.must_change_password ? "/change-password" : (u?.__new ? "/onboarding" : "/labs");

  const onBooted = () => { sessionStorage.setItem("hmg_booted", "1"); setPhase("LOGIN_SCREEN"); };

  const submit = async (e) => {
    e?.preventDefault();
    if (phase === "AUTHENTICATING") return;
    setError("");
    setPhase("AUTHENTICATING");
    try {
      if (mode === "signup") {
        const { data } = await api.post("/auth/register", { name: name.trim(), email: email.trim(), password });
        localStorage.setItem("token", data.token);
        await refresh();
        setPendingUser({ ...data.user, __new: true });
      } else {
        const u = await login(email.trim(), password);
        setPendingUser(u);
      }
      setPhase("STARTING_SESSION");
    } catch (err) {
      let msg;
      if (mode === "signup") {
        msg = apiErr(err) || "Could not create your account. Please try again.";
      } else {
        const status = err?.response?.status;
        if (status === 401) msg = "Incorrect email or password. Please try again.";
        else if (status === 403) msg = apiErr(err) || "This account can't sign in yet.";
        else if (!err?.response) msg = "Can't reach the server right now. Check your connection and try again.";
        else msg = apiErr(err) || "Something went wrong signing you in. Please try again shortly.";
      }
      setError(msg);
      setShake(true);
      setPassword("");
      setPhase("LOGIN_SCREEN");
      setTimeout(() => setShake(false), 600);
    }
  };

  const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const date = now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const avatarInitial = (email.split("@")[0] || "u")[0]?.toUpperCase();

  if (phase === "BOOTING") return <LinuxBootScreen onComplete={onBooted} />;
  if (phase === "RESTARTING") return <LinuxShutdownScreen variant="restart" onComplete={() => setPhase("BOOTING")} />;
  if (phase === "SHUTTING_DOWN") return <LinuxShutdownScreen variant="poweroff" onPowerOn={() => setPhase("BOOTING")} />;
  if (phase === "SUSPENDED") return (
    <button className="fixed inset-0 z-50 bg-black text-white flex flex-col items-center justify-center gdm-fade" data-testid="linux-suspended" onClick={() => setPhase("LOGIN_SCREEN")}>
      <div className="font-display text-6xl font-bold tabular-nums">{time}</div>
      <div className="text-white/50 mt-2">{date}</div>
      <div className="text-white/30 text-xs mt-8">Suspended — click to resume</div>
    </button>
  );
  if (phase === "STARTING_SESSION") return <SessionLoader onDone={() => navigate(destFor(pendingUser), { replace: true })} />;

  const authenticating = phase === "AUTHENTICATING";

  return (
    <div
      className="fixed inset-0 z-40 overflow-hidden text-white gdm-fade select-none"
      data-testid="linux-display-manager"
      style={{ backgroundImage: `url(${WALLPAPER})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-[#06080c]/55 backdrop-blur-[2px]" />

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 h-9 flex items-center px-4 text-xs text-white/80 z-10">
        <span className="font-medium tracking-wide">HMG Linux</span>
        <span className="mx-auto tabular-nums">{time} · {date}</span>
        <div className="flex items-center gap-3 relative">
          <button data-testid="gdm-status-btn" onClick={() => { setQsOpen((v) => !v); setPowerOpen(false); }} className="flex items-center gap-2 hover:bg-white/10 rounded px-2 py-1">
            <Wifi className={`h-4 w-4 ${wifi ? "" : "opacity-40"}`} />
            <Volume2 className="h-4 w-4" />
            <span className="text-[11px]">en</span>
          </button>
          <button data-testid="gdm-power-btn" onClick={() => { setPowerOpen((v) => !v); setQsOpen(false); }} className="hover:bg-white/10 rounded px-1.5 py-1"><Power className="h-4 w-4" /></button>

          {qsOpen && (
            <div className="absolute right-0 top-9 w-64 rounded-xl bg-[#161a22]/95 border border-white/10 backdrop-blur-xl shadow-2xl p-3 gdm-rise" data-testid="gdm-quick-settings">
              <button onClick={() => setWifi((v) => !v)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/10">
                <span className="flex items-center gap-2 text-sm"><Wifi className="h-4 w-4" /> Wi-Fi</span>
                <span className={`text-xs ${wifi ? "text-[#38bdf8]" : "text-white/40"}`}>{wifi ? "HMG-Net" : "Off"}</span>
              </button>
              <div className="px-3 py-2">
                <div className="flex items-center gap-2 text-sm mb-1.5"><Volume2 className="h-4 w-4" /> Volume</div>
                <input type="range" defaultValue={65} className="w-full accent-[#38bdf8]" aria-label="Volume" />
              </div>
              <div className="px-3 py-2">
                <div className="flex items-center gap-2 text-sm mb-1.5"><SettingsIcon className="h-4 w-4" /> Brightness</div>
                <input type="range" defaultValue={80} className="w-full accent-[#38bdf8]" aria-label="Brightness" />
              </div>
            </div>
          )}

          {powerOpen && (
            <div className="absolute right-0 top-9 w-52 rounded-xl bg-[#161a22]/95 border border-white/10 backdrop-blur-xl shadow-2xl p-1.5 gdm-rise" data-testid="gdm-power-menu">
              <button data-testid="power-suspend" onClick={() => { setPowerOpen(false); setPhase("SUSPENDED"); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 text-sm"><Moon className="h-4 w-4" /> Suspend</button>
              <button data-testid="power-restart" onClick={() => { setPowerOpen(false); setPhase("RESTARTING"); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 text-sm"><RotateCcw className="h-4 w-4" /> Restart</button>
              <button data-testid="power-off" onClick={() => { setPowerOpen(false); setPhase("SHUTTING_DOWN"); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 text-sm text-red-300"><Power className="h-4 w-4" /> Power Off</button>
            </div>
          )}
        </div>
      </div>

      {(qsOpen || powerOpen) && <div className="absolute inset-0 z-0" onClick={() => { setQsOpen(false); setPowerOpen(false); }} />}

      {/* Center login */}
      <div className="relative z-[1] h-full flex flex-col items-center justify-center px-6">
        <div className="text-center mb-8 gdm-rise">
          <div className="font-display text-7xl font-bold tabular-nums tracking-tight drop-shadow">{time}</div>
          <div className="text-white/60 mt-1">{date}</div>
        </div>

        <form
          ref={cardRef}
          onSubmit={submit}
          data-testid="auth-form"
          className={`w-full max-w-sm rounded-2xl bg-[#0f131b]/70 border border-white/10 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] p-7 gdm-rise ${shake ? "gdm-shake" : ""}`}
          style={{ animationDelay: "0.1s" }}
        >
          <div className="flex flex-col items-center">
            <div className="h-20 w-20 rounded-full bg-gradient-to-b from-[#1f2734] to-[#0d1219] border border-white/10 grid place-items-center text-2xl font-semibold shadow-inner">
              {email ? avatarInitial : <User className="h-9 w-9 text-white/60" />}
            </div>
            <div className="mt-3 font-medium">{mode === "signup" ? "Create your account" : (email ? email.split("@")[0] : "Sign in")}</div>
            <div className="text-xs text-white/45">HMG Linux Lab</div>
          </div>

          <div className="mt-6 space-y-3">
            {mode === "signup" && (
              <div>
                <label htmlFor="name" className="sr-only">Full name</label>
                <input
                  id="name"
                  type="text"
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  autoComplete="name"
                  className="w-full h-11 rounded-lg bg-white/5 border border-white/10 px-3.5 text-sm text-white placeholder:text-white/35 outline-none focus:border-[#38bdf8]/60 focus:ring-2 focus:ring-[#38bdf8]/30 transition"
                />
              </div>
            )}
            <div>
              <label htmlFor="email" className="sr-only">Username</label>
              <input
                id="email"
                type="email"
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Username (email)"
                autoComplete="username"
                autoFocus
                className="w-full h-11 rounded-lg bg-white/5 border border-white/10 px-3.5 text-sm text-white placeholder:text-white/35 outline-none focus:border-[#38bdf8]/60 focus:ring-2 focus:ring-[#38bdf8]/30 transition"
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                type={showPw ? "text" : "password"}
                data-testid="password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                className="w-full h-11 rounded-lg bg-white/5 border border-white/10 px-3.5 pr-10 text-sm text-white placeholder:text-white/35 outline-none focus:border-[#38bdf8]/60 focus:ring-2 focus:ring-[#38bdf8]/30 transition"
              />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-1" aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {error && <div className="text-sm text-red-300 text-center gdm-fade" data-testid="login-error" role="alert">{error}</div>}

            <button
              type="submit"
              data-testid="submit-auth"
              disabled={authenticating || !email || !password || (mode === "signup" && !name)}
              className="w-full h-11 rounded-lg bg-[#38bdf8] text-[#06121b] font-semibold flex items-center justify-center gap-2 hover:bg-[#5cc9fb] disabled:opacity-50 transition-colors shadow-[0_0_24px_rgba(56,189,248,0.3)]"
            >
              {authenticating ? <><Loader2 className="h-4 w-4 gdm-spin" /> {mode === "signup" ? "Creating account…" : "Authenticating…"}</> : <>{mode === "signup" ? "Create account" : "Sign in"} <ArrowRight className="h-4 w-4" /></>}
            </button>

            <button
              type="button"
              data-testid="toggle-auth-mode"
              onClick={() => { setError(""); setPassword(""); setMode((m) => m === "signin" ? "signup" : "signin"); }}
              className="w-full text-center text-[13px] text-white/60 hover:text-white transition-colors"
            >
              {mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
            </button>
            {mode === "signin" && (
              <button type="button" data-testid="forgot-password-link" onClick={() => navigate("/forgot-password")}
                className="w-full text-center text-[12px] text-white/40 hover:text-white/80 transition-colors">
                Forgot password?
              </button>
            )}
          </div>

          <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-white/40">
            <ChevronRight className="h-3 w-3" /> Session: HMG Linux Desktop
          </div>
        </form>

        <p className="mt-6 text-[11px] text-white/35 gdm-rise" style={{ animationDelay: "0.2s" }}>
          {mode === "signup" ? "Create an account, then subscribe to the labs you need." : "Sign in to continue to your labs."}
        </p>

        <InstallPrepIT />
      </div>

      {/* Bottom-right: accessibility + full screen */}
      <div className="absolute bottom-4 right-4 flex items-center gap-2 z-10">
        <button className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 border border-white/10 hover:bg-white/10" title="Accessibility" aria-label="Accessibility"><Accessibility className="h-4 w-4" /></button>
        <button
          data-testid="fullscreen-btn"
          onClick={() => { const el = document.documentElement; if (!document.fullscreenElement) el.requestFullscreen?.().catch(() => {}); else document.exitFullscreen?.(); }}
          className="h-9 px-3 grid place-items-center rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-xs flex-row gap-2"
          title="Toggle full screen"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
